import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import authApi from '../services/authApi';
import { useAuth } from './AuthContext';

interface BadgeCounts {
  UPDATES: number;
  SCREENERS: number;
  LEARNING: number;
}

interface BadgeCtx {
  counts: BadgeCounts;
  markSeen: (section: 'UPDATES' | 'SCREENERS' | 'LEARNING') => Promise<void>;
  refresh: () => void;
}

const defaults: BadgeCounts = { UPDATES: 0, SCREENERS: 0, LEARNING: 0 };
const BadgeContext = createContext<BadgeCtx>({ counts: defaults, markSeen: async () => {}, refresh: () => {} });

const POLL_INTERVAL_MS = 60_000;

export const BadgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<BadgeCounts>(defaults);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!isAuthenticated) { setCounts(defaults); return; }
    try {
      const res = await authApi.getCommunityBadges();
      if (res?.data) setCounts(res.data);
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCounts();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchCounts, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchCounts]);

  const markSeen = useCallback(async (section: 'UPDATES' | 'SCREENERS' | 'LEARNING') => {
    try {
      await authApi.markCommunitySectionSeen(section);
      setCounts(prev => ({ ...prev, [section]: 0 }));
    } catch {}
  }, []);

  return (
    <BadgeContext.Provider value={{ counts, markSeen, refresh: fetchCounts }}>
      {children}
    </BadgeContext.Provider>
  );
};

export const useBadge = () => useContext(BadgeContext);
